import {
  ThreadOrigin,
  type AgentThreadRecord,
} from "@/shared/domain/agentThread";

/**
 * Whether the signed-in user may continue a thread on this page, and if not,
 * what the read-only banner should say.
 *
 * Two reasons a thread is read-only here: it belongs to someone else, or it
 * belongs to another surface. A Slack-origin thread is read-only for everyone
 * (the API refuses new turns from anywhere but Slack), including — in
 * principle — its owner, so that check comes first and does not depend on who
 * is looking. Slack threads are owned by the org's Slack service user, so in
 * practice they would land in the "someone else's" branch anyway; naming the
 * origin explicitly is what lets the banner say where the conversation lives.
 */
export interface ThreadAccess {
  /** ``true`` when the interactive thread should render. */
  canContinue: boolean;
  /** Banner copy for the read-only case; ``null`` when the user can continue. */
  readOnlyNotice: string | null;
}

const SLACK_NOTICE =
  "This conversation lives in Slack and is read-only here. Fork from any assistant message to continue it in your own thread.";
const SOMEONE_ELSES_NOTICE =
  "Read-only view of someone else’s thread. Fork from any assistant message to continue the conversation in your own thread.";

export function describeThreadAccess(
  thread: Pick<AgentThreadRecord, "created_by" | "origin">,
  currentUserId: string,
): ThreadAccess {
  if (thread.origin === ThreadOrigin.SLACK) {
    return { canContinue: false, readOnlyNotice: SLACK_NOTICE };
  }
  if (thread.created_by !== currentUserId) {
    return { canContinue: false, readOnlyNotice: SOMEONE_ELSES_NOTICE };
  }
  return { canContinue: true, readOnlyNotice: null };
}
